// Servicio de usuarios basado en la lista de socias (solo Frontend)
// Los "usuarios" son vistas derivadas de socias; se almacenan en localStorage

import { sociasStore } from './sociasStore';

const USERS_KEY = 'fepast_users_from_socias_v1';

function readUsers() {
  try {
    const raw = localStorage.getItem(USERS_KEY);
    if (!raw) return null;
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : null;
  } catch {
    return null;
  }
}

function writeUsers(list) {
  localStorage.setItem(USERS_KEY, JSON.stringify(list || []));
}

function sociaToUserView(socia, tiposById = {}) {
  const dp = socia?.datosPersonales || {};
  const email = (socia?.datosUbicacion || {}).correoElectronico || '';
  const tipo = dp.tipoSocio || '';
  const roleName = tiposById[tipo]?.TIPOSOCIA_NOMBRE || 'Usuario';
  const username = (dp.nombres?.split(' ')[0] || 'user').toLowerCase().charAt(0)
    + (dp.apellidos?.split(' ')[0] || 'demo').toLowerCase();
  return {
    id: socia.id,
    fullName: `${dp.nombres || ''} ${dp.apellidos || ''}`.trim(),
    username,
    email,
    status: (socia?.datosMedicos?.estado || 'Activo'),
    permissions: [roleName]
  };
}

export const usersService = {
  // Construye usuarios desde socias y cachea en localStorage para edición de permisos/estado
  async list(tiposSociaFetcher) {
    const cached = readUsers();
    if (cached) return cached;
    const socias = sociasStore.getAll();
    let tiposById = {};
    try {
      if (typeof tiposSociaFetcher === 'function') {
        const tipos = await tiposSociaFetcher();
        const data = Array.isArray(tipos?.data) ? tipos.data : (Array.isArray(tipos) ? tipos : []);
        tiposById = (data || []).reduce((acc, t) => { acc[t.TS] = t; return acc; }, {});
      }
    } catch {
      tiposById = {};
    }
    const users = socias.map(s => sociaToUserView(s, tiposById));
    writeUsers(users);
    return users;
  },
  async getById(id) {
    const users = readUsers() || (await this.list());
    return users.find(u => u.id === id) || null;
  },
  async setStatus(id, status) {
    const users = readUsers() || (await this.list());
    const updated = users.map(u => u.id === id ? { ...u, status } : u);
    writeUsers(updated);
    // Reflejar también en socias
    sociasStore.setEstado(id, status);
    return updated.find(u => u.id === id) || null;
  },
  async updatePermissions(id, permissions) {
    const users = readUsers() || (await this.list());
    let updated;
    if (!users.find(u => u.id === id)) {
      // Si no existe, crear un usuario básico con este ID y permisos
      const newUser = {
        id,
        fullName: '',
        username: '',
        email: '',
        status: 'Activo',
        permissions: [...permissions]
      };
      updated = [...users, newUser];
    } else {
      updated = users.map(u => u.id === id ? { ...u, permissions: [...permissions] } : u);
    }
    writeUsers(updated);
    return updated.find(u => u.id === id) || null;
  },
  upsertUser(user) {
    const users = readUsers() || [];
    const idx = users.findIndex(u => u.id === user.id);
    if (idx === -1) users.push(user); else users[idx] = { ...users[idx], ...user };
    writeUsers(users);
    return user;
  },
  // Cuando se registra una nueva socia, crear/actualizar vista de usuario
  syncFromSocia(socia, tiposById = {}) {
    const userView = sociaToUserView(socia, tiposById);
    const users = readUsers() || [];
    const idx = users.findIndex(u => u.id === userView.id);
    if (idx === -1) users.push(userView); else users[idx] = userView;
    writeUsers(users);
    return userView;
  },
  resetCache() { writeUsers([]); }
};

export default usersService;
